import { useState } from 'react';
import type { NavGroup } from './types';
import { Icon } from './Icon';

interface SideNavProps {
  groups: NavGroup[];
  currentPath: string;
  collapsed: boolean;
  onNavigate: (path: string) => void;
  sidenavAriaLabel?: string;
  brandName?: string;
  groupCollapseLabel?: string;
  groupExpandLabel?: string;
}

interface TooltipProps {
  label: string;
  visible: boolean;
}

function Tooltip({ label, visible }: TooltipProps) {
  if (!visible) return null;
  return (
    <span
      role="tooltip"
      style={{
        position: 'absolute',
        left: 'calc(100% + var(--spacing-2))',
        top: '50%',
        transform: 'translateY(-50%)',
        background: 'var(--color-text-primary)',
        color: 'var(--color-text-inverse)',
        fontSize: 'var(--font-size-xs)',
        padding: '4px var(--spacing-2)',
        borderRadius: 'var(--radius-sm)',
        whiteSpace: 'nowrap',
        zIndex: 300,
        pointerEvents: 'none',
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      {label}
    </span>
  );
}

function NavGroupSection({
  group,
  currentPath,
  collapsed,
  onNavigate,
  collapseLabel,
  expandLabel,
}: {
  group: NavGroup;
  currentPath: string;
  collapsed: boolean;
  onNavigate: (path: string) => void;
  collapseLabel: string;
  expandLabel: string;
}) {
  // 자식 중 active 있으면 그룹 자동 펼침
  const hasActive = group.items.some((item) => currentPath === item.path);
  const [expanded, setExpanded] = useState<boolean>(hasActive || true);
  // R7-015 / R8-A-003: aria-controls 연결용 ID — slug 우선, 없으면 key 정규화
  const groupListId = `sidenav-group-${group.slug ?? (group.key.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'g')}`;

  return (
    <div>
      {/* 그룹 레이블 — collapsed 시 구분선만 */}
      {!collapsed && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls={groupListId}
          title={expanded ? collapseLabel : expandLabel}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--spacing-2) var(--spacing-4)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: 'var(--color-sidebar-text-muted)',
            marginTop: 'var(--spacing-3)',
          }}
        >
          {group.label}
          {/* R7-015: chevron 시각적 hint — aria-hidden (버튼이 aria-expanded 보유) */}
          <Icon
            id={expanded ? 'chevron-down' : 'chevron-right'}
            size={12}
          />
        </button>
      )}
      {collapsed && (
        <div
          style={{
            height: '1px',
            background: 'var(--color-sidebar-hover-bg)',
            margin: 'var(--spacing-2) var(--spacing-2)',
          }}
        />
      )}

      {/* 아이템 목록 — R7-015: id 로 aria-controls 연결 */}
      {(expanded || collapsed) && (
        <ul
          id={groupListId}
          role="list"
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
          }}
        >
          {group.items.map((item) => {
            const isActive = currentPath === item.path;
            return (
              <NavItemRow
                key={item.key}
                label={item.label}
                path={item.path}
                icon={item.icon}
                isActive={isActive}
                collapsed={collapsed}
                onNavigate={onNavigate}
              />
            );
          })}
        </ul>
      )}
    </div>
  );
}

// queue #669 §R669-1 — `data-nav-path` 는 메뉴 항목을 path 로 지목하는 generic·재사용 속성
// (투어 전용 아님). 사이드바 라벨은 i18n + DB SSOT 구동이라 텍스트 매칭이 취약해 안정 셀렉터로
// 도입 — OnboardingTour 하이라이트뿐 아니라 e2e 메뉴 셀렉터 drift 완화에도 유익하다.
function NavItemRow({
  label,
  path,
  icon,
  isActive,
  collapsed,
  onNavigate,
}: {
  label: string;
  path: string;
  icon?: string;
  isActive: boolean;
  collapsed: boolean;
  onNavigate: (path: string) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  return (
    <li>
      <button
        type="button"
        aria-current={isActive ? 'page' : undefined}
        data-nav-path={path}
        onClick={() => onNavigate(path)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onNavigate(path);
        }}
        onMouseEnter={() => {
          setHovered(true);
          if (collapsed) setTooltipVisible(true);
        }}
        onMouseLeave={() => {
          setHovered(false);
          setTooltipVisible(false);
        }}
        title={collapsed ? label : undefined}
        style={{
          position: 'relative',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: collapsed ? 0 : 'var(--spacing-3)',
          justifyContent: collapsed ? 'center' : 'flex-start',
          padding: collapsed
            ? 'var(--spacing-3) 0'
            : 'var(--spacing-2) var(--spacing-4)',
          background: isActive
            ? 'var(--color-sidebar-active-bg)'
            : hovered
              ? 'var(--color-sidebar-hover-bg)'
              : 'none',
          border: 'none',
          borderLeft: isActive
            ? '3px solid var(--color-sidebar-active-border)'
            : '3px solid transparent',
          cursor: 'pointer',
          fontSize: 'var(--font-size-sm)',
          fontWeight: isActive ? 600 : 400,
          color: isActive
            ? 'var(--color-brand-primary)'
            : 'var(--color-sidebar-text)',
          textAlign: 'left',
          transition: 'background 0.1s, border-color 0.1s',
          borderRadius: collapsed ? 0 : '0 var(--radius-md) var(--radius-md) 0',
        }}
      >
        {icon && (
          <Icon
            id={icon}
            size={18}
            style={{ flexShrink: 0, color: isActive ? 'var(--color-brand-primary)' : 'var(--color-sidebar-text)' }}
          />
        )}
        {!collapsed && <span>{label}</span>}
        {collapsed && (
          <Tooltip label={label} visible={tooltipVisible} />
        )}
      </button>
    </li>
  );
}

export function SideNav({ groups, currentPath, collapsed, onNavigate, sidenavAriaLabel, brandName, groupCollapseLabel, groupExpandLabel }: SideNavProps) {
  const resolvedSidenavLabel = sidenavAriaLabel ?? '사이드 내비게이션';
  const resolvedBrandName = brandName ?? 'ERAF';
  const resolvedCollapseLabel = groupCollapseLabel ?? '그룹 접기';
  const resolvedExpandLabel = groupExpandLabel ?? '그룹 펼치기';
  return (
    <nav
      aria-label={resolvedSidenavLabel}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        paddingBottom: 'var(--spacing-4)',
      }}
    >
      {/* 로고/브랜드 영역 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: 'var(--spacing-2)',
          padding: collapsed
            ? 'var(--spacing-4) 0'
            : 'var(--spacing-4) var(--spacing-4)',
          height: 'var(--layout-header-height)',
          flexShrink: 0,
          borderBottom: '1px solid var(--color-sidebar-hover-bg)',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: '28px',
            height: '28px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-brand-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="var(--color-text-inverse)"
          >
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="var(--color-text-inverse)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        {!collapsed && (
          <span
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 700,
              color: 'var(--color-sidebar-text)',
              whiteSpace: 'nowrap',
            }}
          >
            {resolvedBrandName}
          </span>
        )}
      </div>

      {/* 메뉴 그룹 목록 */}
      <div style={{ flex: 1, paddingTop: 'var(--spacing-2)' }}>
        {groups.map((group) => (
          <NavGroupSection
            key={group.key}
            group={group}
            currentPath={currentPath}
            collapsed={collapsed}
            onNavigate={onNavigate}
            collapseLabel={resolvedCollapseLabel}
            expandLabel={resolvedExpandLabel}
          />
        ))}
      </div>
    </nav>
  );
}
